import React, { useEffect, useRef, useState } from 'react';
import { SimpleGrid, Box, Icon, Body } from '@hover/blueprint';
import Slider from 'react-slick';
import { iCheck, iChevronLeft, iChevronRight, iX } from '@hover/icons';
import { nanoid } from 'nanoid';
import {
  HOVER_REPORTS_TABLE_HEADER,
  HOVER_REPORTS_TABLE_DATA,
} from '../../../constants';
import HeaderCard from '../../pricing/choose-the-plan/HeaderCard';

const NORMALIZED_MOBILE_DATA = [
  {
    id: 0,
    header: HOVER_REPORTS_TABLE_HEADER[0],
    columns: HOVER_REPORTS_TABLE_DATA.map(tableData => ({
      id: tableData.id,
      value: tableData.roofOnly,
    })),
  },
  {
    id: 1,
    header: HOVER_REPORTS_TABLE_HEADER[1],
    columns: HOVER_REPORTS_TABLE_DATA.map(tableData => ({
      id: tableData.id,
      value: tableData.completeReport,
    })),
  },
];

const settings = {
  dots: false,
  infinite: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: false,
  arrows: false,
};

const Mobile: React.FC = () => {
  const headerRef = useRef<Slider>(null);
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );
  useEffect(() => {
    setWindowWidth(window.innerWidth);
    const onWindowSizeChange = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onWindowSizeChange);
    return () => window.removeEventListener('resize', onWindowSizeChange);
  }, []);

  const getHeight = (index: number): string | number => {
    if (index === 0) {
      if (windowWidth < 346) {
        return '65px';
      }
      return '64px';
    }
    if (index === 1) {
      if (windowWidth < 383) {
        return '209px';
      }
      if (windowWidth < 418) {
        return '185px';
      }
      if (windowWidth < 437) {
        return '161px';
      }
      if (windowWidth < 544) {
        return '137px';
      }
      if (windowWidth < 614) {
        return '113px';
      }
      return '89px';
    }
    if (index === 2) {
      if (windowWidth < 436) {
        return '113px';
      }
      if (windowWidth < 564) {
        return '89px';
      }
      return '65px';
    }
    if (index === 3) {
      if (windowWidth < 402) {
        return '113px';
      }
      if (windowWidth < 485) {
        return '89px';
      }
      return '65px';
    }
    if (index === 4) {
      if (windowWidth < 380) {
        return '113px';
      }
      if (windowWidth < 470) {
        return '89px';
      }
      if (windowWidth < 786) {
        return '65px';
      }
      return '64px';
    }
    if (index === 5) {
      if (windowWidth < 409) {
        return '65px';
      }
    }
    if (index === 6) {
      if (windowWidth < 430) {
        return '113px';
      }
      if (windowWidth < 585) {
        return '89px';
      }
      return '65px';
    }
    if (index === 7) {
      if (windowWidth < 415) {
        return '113px';
      }
      if (windowWidth < 509) {
        return '89px';
      }
      return '65px';
    }
    if (index === 8) {
      if (windowWidth < 397) {
        return '89px';
      }
      if (windowWidth < 587) {
        return '65px';
      }
    }
    if (index === 9) {
      if (windowWidth < 378) {
        return '161px';
      }
      if (windowWidth < 451) {
        return '137px';
      }
      if (windowWidth < 544) {
        return '113px';
      }
      if (windowWidth < 727) {
        return '89px';
      }
      return '65px';
    }
    if (index === 10) {
      if (windowWidth < 389) {
        return '233px';
      }
      if (windowWidth < 396) {
        return '209px';
      }
      if (windowWidth < 455) {
        return '185px';
      }
      if (windowWidth < 491) {
        return '161px';
      }
      if (windowWidth < 606) {
        return '137px';
      }
      if (windowWidth < 711) {
        return '113px';
      }
      return '89px';
    }
    if (index === 11) {
      if (windowWidth < 433) {
        return '89px';
      }
      if (windowWidth < 707) {
        return '65px';
      }
    }
    return '64px';
  };

  const onNext = () => {
    if (headerRef.current) {
      headerRef.current.slickNext();
    }
  };

  const onPrev = () => {
    if (headerRef.current) {
      headerRef.current.slickPrev();
    }
  };

  return (
    <SimpleGrid
      columns={2}
      d={{ base: 'grid', tablet: 'none' }}
      spacing={0}
      w="100%"
    >
      <Box pl={500} pt="212px" flexDir="column" shadow="distance300" w="100%">
        {HOVER_REPORTS_TABLE_DATA.map(data => (
          <Box
            minH="64px"
            py={200}
            mt={0}
            alignItems="center"
            key={data.id}
            borderTopWidth="1px"
            borderColor="neutral.400"
            pr={500}
            pl={400}
          >
            <Body size={500}>{data.title}</Body>
          </Box>
        ))}
      </Box>
      <Box d="block" alignItems="center">
        <Box
          justifyContent="space-between"
          alignItems="center"
          w="100%"
          mb={500}
          pr={500}
          pl={400}
        >
          <Icon
            color="primary.600"
            strokeWidth="2px"
            icon={iChevronLeft}
            size="default"
            m={0}
            zIndex={10}
            onClick={onPrev}
          />
          <Icon
            zIndex={10}
            onClick={onNext}
            strokeWidth="2px"
            color="primary.600"
            icon={iChevronRight}
            size="default"
            m={0}
          />
        </Box>
        <Box w="100%" d="block">
          <Slider ref={headerRef} {...settings}>
            {NORMALIZED_MOBILE_DATA.map(section => (
              <Box key={section.id} flexDir="column">
                <Box
                  flexDir="column"
                  alignItems="center"
                  mb={500}
                  pl={400}
                  pr={500}
                >
                  <Box px="6px" w="100%">
                    <HeaderCard w="100%" card={section.header} h="140px" />
                  </Box>
                </Box>
                {section.columns.map((column, index) => (
                  <Box
                    w="100%"
                    key={nanoid()}
                    alignItems="center"
                    minH="64px"
                    borderTopWidth="1px"
                    borderColor="neutral.400"
                    h={getHeight(index)}
                  >
                    <Icon
                      icon={column.value ? iCheck : iX}
                      size="default"
                      mx="auto"
                      color={column.value ? 'success.600' : 'brandOrange.300'}
                    />
                  </Box>
                ))}
              </Box>
            ))}
          </Slider>
        </Box>
      </Box>
    </SimpleGrid>
  );
};

export default Mobile;
